import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import api, { API_BASE_URL } from './services/api';
import { useBrandingStore } from './context/brandingStore';
import { installFreshAfterWrite } from './lib/freshAfterWrite';
import App from './App';
import StagingRibbon from './components/common/StagingRibbon';
import Toaster from './components/common/Toaster';
import './styles/index.css';

installFreshAfterWrite();

function render() {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <StagingRibbon />
          <App />
          <Toaster />
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

// Whose portal is this? Asked once, before anything renders and before any
// auth check, so the first paint already carries the right title, favicon,
// colour and name — no flash of somebody else's brand, and the money and
// date formatters know their locale from their first call. `load()` never
// rejects: an unreachable resolve endpoint means neutral defaults, not a
// blank page. index.html's background colour covers the wait.
//
// On an MSP's own domain (portal.acme.com — Phase 5a) the hostname says
// nothing about which tenant it is, so the API host cannot be derived from
// it; the resolve answer carries the tenant's API host instead. Adopting it
// here, before the first authenticated call, is what makes a custom domain
// talk to its own tenant rather than to whatever the apex API host would
// have guessed. Only the *host* is taken: the backend's API_URL has no path
// while VITE_API_URL ends in /api, so swapping the whole string would drop
// the path from every call. A tenant subdomain already derives the same
// host and the swap is a no-op there.
function adoptResolvedApiHost() {
  try {
    const resolved = useBrandingStore.getState().apiBase;
    if (!resolved || !/^https?:\/\//i.test(API_BASE_URL)) return;
    const current = new URL(API_BASE_URL);
    const target = new URL(resolved);
    if (current.host === target.host) return;
    current.protocol = target.protocol;
    current.host = target.host;
    api.defaults.baseURL = current.toString().replace(/\/+$/, '');
  } catch {
    // A malformed answer is not a reason to stop rendering; the configured host stands.
  }
}

useBrandingStore
  .getState()
  .load(API_BASE_URL)
  .finally(() => {
    adoptResolvedApiHost();
    render();
  });
